
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { toast } from "@/hooks/use-toast";

interface PaymentErrorProps {
  error?: string; // Keep for backward compatibility
  errorMessage?: string | null;
  verificationAttempts?: number;
  onRetry?: () => void;
  webhookProcessed?: boolean;
}

export const PaymentError = ({ 
  error, 
  errorMessage, 
  verificationAttempts = 0, 
  onRetry, 
  webhookProcessed = false 
}: PaymentErrorProps) => {
  const navigate = useNavigate();
  const maxAttempts = 3;
  const canRetry = verificationAttempts < maxAttempts;
  
  // Use error or errorMessage, prioritizing errorMessage
  const displayError = errorMessage || error;
  
  // Log error details when component renders
  useEffect(() => {
    console.error("[PaymentError] Payment error encountered:", {
      error,
      errorMessage,
      verificationAttempts,
      webhookProcessed,
      canRetry,
      timestamp: new Date().toISOString()
    });
    
    // If this is the last attempt, show additional guidance about enhanced recovery
    if (verificationAttempts >= maxAttempts - 1 && !webhookProcessed) {
      toast({
        title: "Enhanced Recovery Available",
        description: "Our enhanced recovery system can help resolve booking creation issues. Look for the Enhanced Recovery button below.",
        variant: "default",
        duration: 10000
      });
    }
  }, [error, errorMessage, verificationAttempts, webhookProcessed]);
  
  const isConcurrencyError = displayError?.includes('serialization') || 
                            displayError?.includes('concurrency') || 
                            displayError?.includes('conflicts') ||
                            displayError?.includes('tuple to be updated was already modified');
  
  return (
    <div className="flex flex-col items-center">
      <div className="bg-red-100 rounded-full p-4 mb-6">
        <AlertCircle className="h-16 w-16 text-red-600" />
      </div>
      <h2 className="text-xl font-semibold mb-2">
        {webhookProcessed ? "Payment Processing" : "Payment Verification Issue"}
      </h2>
      <p className="text-center text-tribal-neutral mb-2">
        {webhookProcessed 
          ? "Your payment is being processed by our enhanced system. Please check your email for confirmation."
          : isConcurrencyError 
            ? "We detected a database concurrency issue. Our enhanced recovery system can resolve this."
            : "There was a problem verifying your payment."}
      </p>
      {displayError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-4 w-full">
          <p className="text-sm text-red-700">{displayError}</p>
          {verificationAttempts > 0 && !webhookProcessed && (
            <p className="text-sm text-red-700 mt-1">
              Verification attempted {verificationAttempts} time{verificationAttempts !== 1 ? 's' : ''}.
              {canRetry && ` ${maxAttempts - verificationAttempts} attempt${maxAttempts - verificationAttempts !== 1 ? 's' : ''} remaining.`}
            </p>
          )}
          {isConcurrencyError && (
            <p className="text-sm text-blue-700 mt-2 font-medium">
              💡 This appears to be a concurrency issue that our enhanced recovery system can fix.
            </p>
          )}
        </div>
      )}
      <p className="text-center text-tribal-neutral mb-6">
        {webhookProcessed 
          ? "If you don't receive an email within 15 minutes, please contact our support team."
          : isConcurrencyError
            ? "Use the Enhanced Recovery option below to resolve database conflicts automatically."
            : "Please try again or use enhanced recovery if the issue persists."}
      </p>
      <div className="flex flex-col sm:flex-row gap-4">
        {canRetry && !webhookProcessed && onRetry && (
          <Button 
            variant="outline" 
            onClick={() => {
              console.log("[PaymentError] Retrying verification, attempt #", verificationAttempts + 1);
              toast({
                title: "Retrying Verification",
                description: `Attempt ${verificationAttempts + 1} of ${maxAttempts} with enhanced error handling`,
                variant: "default"
              });
              onRetry();
            }}
            className="mb-2 sm:mb-0"
            disabled={!canRetry}
          >
            {verificationAttempts < maxAttempts - 1 ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Retry Verification
              </>
            ) : (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Final Retry Attempt
              </>
            )}
          </Button>
        )}
        <Button 
          className="bg-tribal-primary hover:bg-tribal-primary/90"
          onClick={() => {
            console.log("[PaymentError] Navigating back to cart");
            navigate("/cart");
          }}
        >
          Return to Cart
        </Button>
      </div>
      
      {/* Enhanced guidance for concurrency issues */}
      {verificationAttempts >= maxAttempts && !webhookProcessed && (
        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-md w-full text-sm">
          <p className="font-semibold mb-1 text-blue-800">Enhanced Recovery System Available</p>
          <p className="text-blue-700">Our enhanced recovery system includes:</p>
          <ul className="list-disc ml-4 mt-2 text-blue-700">
            <li>Advanced concurrency conflict resolution</li>
            <li>Automatic retry with exponential backoff</li>
            <li>Database-level advisory locking</li>
            <li>Real-time site availability updates</li>
            <li>Automatic email confirmation after recovery</li>
          </ul>
          <p className="mt-2 text-green-700 font-medium">
            Look for the "Enhanced Recovery" button to resolve this issue automatically.
          </p>
        </div>
      )}
    </div>
  );
};
